import React, { useCallback, useEffect, useRef, useState } from 'react'
import { observabilityAPI } from '@hpanel/web-common/api/client'
import type { SSEStatus } from '@hpanel/web-common/api/client'
import type { TenantObservabilityStats } from '@hpanel/web-common/api/types'
import { Sparkline } from './Sparkline'
import { Cpu, MemoryStick, RefreshCw } from 'lucide-react'

/**
 * StatsPanel — two stat cards (CPU%, RAM) with an inline-SVG sparkline.
 *
 * Consumes the per-domain stats SSE stream. Holds the last `MAX_SAMPLES`
 * samples in component state — that's a 5-minute window at the contract's
 * 2-second cadence. Null cgroup values render as em-dash, never as 0 (per
 * the brief — a missing reading is meaningfully different from "idle").
 *
 * Degraded mode (after a 429 or 5x retry): stops the stream and exposes a
 * manual "Refresh" button that one-shots `snapshotStats`. No banner here;
 * the LogTerminal already surfaces the server detail.
 */

interface StatsPanelProps {
  domainId: number
}

const MAX_SAMPLES = 150

/** Render a numeric value as a percentage string, or em-dash if null. */
const fmtPct = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v) ? '—' : `${v.toFixed(1)}%`

/** Render a byte count as a human-friendly string (MB / GB), or em-dash. */
const fmtBytes = (v: number | null | undefined): string => {
  if (v == null || !Number.isFinite(v)) return '—'
  if (v >= 1024 ** 3) return `${(v / 1024 ** 3).toFixed(2)} GB`
  if (v >= 1024 ** 2) return `${(v / 1024 ** 2).toFixed(1)} MB`
  if (v >= 1024) return `${(v / 1024).toFixed(0)} KB`
  return `${v} B`
}

export const StatsPanel: React.FC<StatsPanelProps> = ({ domainId }) => {
  const [samples, setSamples] = useState<TenantObservabilityStats[]>([])
  const [status, setStatus] = useState<SSEStatus>('connecting')
  const [refreshing, setRefreshing] = useState(false)
  const [refreshError, setRefreshError] = useState<string | null>(null)
  // Server-sent stream errors (404 not-found, 502 gateway, 429 cap...). These
  // were console.error-only, so the panel rendered its em-dash skeleton and the
  // user saw a permanently "loading" widget with no indication anything had
  // failed. CLAUDE.md §9 requires the server's detail to REACH THE USER; the
  // dev-console half of that contract was met and the user-facing half was not.
  const [streamError, setStreamError] = useState<string | null>(null)
  const handleRef = useRef<{ close: () => void } | null>(null)

  // Wire up the SSE stream on mount + when domainId changes.
  useEffect(() => {
    handleRef.current?.close()
    setStatus('connecting')
    setSamples([])

    const handle = observabilityAPI.streamStats(domainId, {
      onStats: (s) => {
        setSamples((prev) => {
          const next = prev.length >= MAX_SAMPLES
            ? [...prev.slice(prev.length - MAX_SAMPLES + 1), s]
            : [...prev, s]
          return next
        })
      },
      onError: (message) => {
        // eslint-disable-next-line no-console
        console.error('[observability] stats stream server-error:', message)
        setStreamError(message)
      },
      onStatusChange: (next, errorDetail) => {
        setStatus(next)
        // The degraded transition carries the server's own detail (404/429
        // recovered by openSSE's probe). Dropping it — as this did — is why a
        // tenant opening a domain they don't own saw an endlessly "loading"
        // widget instead of "Not found".
        if (next === 'degraded' && errorDetail) setStreamError(errorDetail)
      },
    })
    handleRef.current = handle

    return () => {
      handle.close()
      handleRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId])

  // Manual snapshot — used when the live stream has degraded (429 / 5x fail).
  const handleManualRefresh = useCallback(async () => {
    setRefreshing(true)
    setRefreshError(null)
    try {
      const snap = await observabilityAPI.snapshotStats(domainId)
      setSamples((prev) => {
        const next = prev.length >= MAX_SAMPLES
          ? [...prev.slice(prev.length - MAX_SAMPLES + 1), snap]
          : [...prev, snap]
        return next
      })
    } catch (err: unknown) {
      setRefreshError(err instanceof Error ? err.message : 'Failed to refresh stats')
    } finally {
      setRefreshing(false)
    }
  }, [domainId])

  const last = samples.length > 0 ? samples[samples.length - 1] : null
  const cpuSeries = samples.map((s) => s.cpu_percent)
  const memSeries = samples.map((s) => s.mem_percent)

  const isDegraded = status === 'degraded'

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3" data-testid="observability-stats-panel">
      {/* CPU card */}
      <div
        data-testid="observability-stats-cpu"
        className="rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 p-3"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <Cpu className="h-3.5 w-3.5 text-gray-500" strokeWidth={1.75} />
            <span className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              CPU
            </span>
          </div>
          <span
            data-testid="observability-stats-cpu-value"
            className="text-[14px] font-medium text-gray-900 dark:text-gray-100 tabular-nums"
          >
            {fmtPct(last?.cpu_percent)}
          </span>
        </div>
        <div className="mt-2 text-blue-600 dark:text-blue-400">
          <Sparkline
            data={cpuSeries}
            min={0}
            max={100}
            width={240}
            height={42}
            testId="observability-stats-cpu-sparkline"
            ariaLabel="CPU utilization sparkline"
          />
        </div>
      </div>

      {/* Memory card */}
      <div
        data-testid="observability-stats-mem"
        className="rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 p-3"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <MemoryStick className="h-3.5 w-3.5 text-gray-500" strokeWidth={1.75} />
            <span className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Memory
            </span>
          </div>
          <span
            data-testid="observability-stats-mem-value"
            className="text-[14px] font-medium text-gray-900 dark:text-gray-100 tabular-nums"
          >
            {fmtPct(last?.mem_percent)}
          </span>
        </div>
        <div
          className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 tabular-nums"
          data-testid="observability-stats-mem-bytes"
        >
          {fmtBytes(last?.mem_used_bytes)} / {fmtBytes(last?.mem_limit_bytes)}
        </div>
        <div className="mt-1 text-emerald-600 dark:text-emerald-400">
          <Sparkline
            data={memSeries}
            min={0}
            max={100}
            width={240}
            height={42}
            testId="observability-stats-mem-sparkline"
            ariaLabel="Memory utilization sparkline"
          />
        </div>
      </div>

      {/* Degraded controls — manual refresh + error surface. */}
      {isDegraded && (
        <div className="sm:col-span-2 flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-amber-50 dark:bg-amber-900/20 text-[12px] text-amber-800 dark:text-amber-200">
          <span data-testid="observability-stats-degraded">
            Live updates paused. Use Refresh for the latest readings.
          </span>
          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={refreshing}
            data-testid="observability-stats-refresh"
            className="h-7 inline-flex items-center gap-1 px-2 rounded-md border border-amber-300 dark:border-amber-700 text-[11px] font-medium text-amber-900 dark:text-amber-100 bg-white/60 dark:bg-amber-900/30 hover:bg-white dark:hover:bg-amber-900/40 disabled:opacity-50"
          >
            <RefreshCw
              className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`}
              strokeWidth={1.75}
            />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      )}
      {refreshError && (
        <div
          className="sm:col-span-2 px-2 py-1 text-[11px] text-red-600 dark:text-red-300"
          data-testid="observability-stats-refresh-error"
        >
          {refreshError}
        </div>
      )}
      {streamError && (
        <div
          role="alert"
          className="sm:col-span-2 px-2 py-1 text-[11px] text-red-600 dark:text-red-300"
          data-testid="observability-stats-stream-error"
        >
          {streamError}
        </div>
      )}
    </div>
  )
}

export default StatsPanel
